import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import {
  NavLink, Outlet, useLocation, useNavigate, useParams,
} from 'react-router-dom';
import { useIntl } from '@edx/frontend-platform/i18n';

import { useCohortDetail, useHeaderFooter } from '../../hooks';
import type { CohortDetail } from '../../types';

import messages from './messages';

import '../cohortSubpage.scss';
import './index.scss';

export type CohortView = 'members' | 'info' | 'discussions';

interface CohortLayoutContextValue {
  detail: CohortDetail;
  /** Push an updated detail up so the chrome reflects an inline edit. */
  setDetail: (detail: CohortDetail) => void;
  isMember: boolean;
  canModerate: boolean;
}

const CohortLayoutContext = createContext<CohortLayoutContextValue | null>(null);

/**
 * Read the parent CohortLayout's loaded cohort detail. Never returns null
 * inside a cohort child view — the layout renders nested routes only once
 * `detail` is present.
 */
export const useCohortLayoutContext = (): CohortLayoutContextValue => {
  const context = useContext(CohortLayoutContext);
  if (!context) {
    throw new Error('useCohortLayoutContext must be used within CohortLayout');
  }
  return context;
};

interface CohortLayoutProps {
  children?: React.ReactNode;
}

const viewFromPathname = (pathname: string): CohortView => {
  if (pathname.endsWith('/info')) {
    return 'info';
  }
  if (pathname.endsWith('/discussions')) {
    return 'discussions';
  }
  return 'members';
};

/**
 * Chrome for the cohort detail area: site header/footer, a left sidebar (cohort
 * summary + navigation) and a breadcrumb, with the active sub-view rendered
 * through <Outlet/>. Fetches the cohort detail once and shares it with the
 * nested Members / Cohort Info / Discussions views via context.
 */
const CohortLayout: React.FC<CohortLayoutProps> = ({ children }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { slug } = useParams<{ slug: string }>();
  const { headerContent, footerContent } = useHeaderFooter(children);

  // The layout only needs the cohort detail + role; the Members sub-view fetches
  // progress from its own `/progress/` endpoint.
  const {
    detail: fetchedDetail,
    detailLoaded,
    detailErrorStatus,
    isMember,
    canModerate,
  } = useCohortDetail(slug);

  // A child edit can push an updated detail back up so the sidebar/breadcrumb
  // reflect it without a refetch. Cleared on cohort change.
  const [detailOverride, setDetailOverride] = useState<CohortDetail | null>(null);
  const detail = detailOverride ?? fetchedDetail;
  const setDetail = useCallback((next: CohortDetail) => setDetailOverride(next), []);

  useEffect(() => {
    setDetailOverride((prev) => (prev?.slug === slug ? prev : null));
  }, [slug]);

  const isRedirecting = detailErrorStatus === 403 || detailErrorStatus === 404;
  const showDetailError = detailLoaded && !detail && !isRedirecting;

  useEffect(() => {
    if (isRedirecting) {
      navigate('/cohorts');
    }
  }, [isRedirecting, navigate]);

  const currentView = viewFromPathname(pathname);

  const viewLabel = (view: CohortView): string => {
    switch (view) {
      case 'info':
        return intl.formatMessage(messages.navInfo);
      case 'discussions':
        return intl.formatMessage(messages.navDiscussions);
      default:
        return intl.formatMessage(messages.navMembers);
    }
  };

  // Every member reads Members + Info; Discussions moderation is owner/co-facilitator only.
  let navViews: CohortView[] = ['info'];
  if (canModerate) {
    navViews = ['members', 'info', 'discussions'];
  } else if (isMember) {
    navViews = ['members', 'info'];
  }

  const navTo = (view: CohortView): string => (
    view === 'members' ? `/cohorts/${slug}` : `/cohorts/${slug}/${view}`
  );

  const contextValue = useMemo<CohortLayoutContextValue>(
    () => ({
      detail: detail as CohortDetail, setDetail, isMember, canModerate,
    }),
    [detail, setDetail, isMember, canModerate],
  );

  return (
    <>
      <input type="hidden" id="anPageName" name="page" defaultValue="cohort-detail" />
      <div className="container-center-horizontal">
        <div className="cohort-detail screen cohort-subpage">
          <div className="homepage">
            {headerContent}
          </div>
          <div className="legatum-container">
            <div className="c-section">
              {showDetailError && (
                <p className="form-error form-error-custom">
                  {intl.formatMessage(messages.detailLoadError)}
                </p>
              )}
              {detailLoaded && detail && (
                <>
                  <nav
                    className="cohort-layout-breadcrumb"
                    aria-label={intl.formatMessage(messages.breadcrumbAriaLabel)}
                  >
                    <NavLink
                      className="cohort-layout-breadcrumb-link"
                      to="/cohorts/mine"
                    >
                      {intl.formatMessage(messages.breadcrumbMyCohorts)}
                    </NavLink>
                    <span className="cohort-layout-breadcrumb-sep" aria-hidden="true">›</span>
                    <span className="cohort-layout-breadcrumb-item">{detail.name}</span>
                    <span className="cohort-layout-breadcrumb-sep" aria-hidden="true">›</span>
                    <span
                      className="cohort-layout-breadcrumb-item cohort-layout-breadcrumb-current"
                      aria-current="page"
                    >
                      {viewLabel(currentView)}
                    </span>
                  </nav>
                  <div className="cohort-layout">
                    <aside className="cohort-layout-sidebar">
                      <h1 className="cohort-layout-sidebar-name">{detail.name}</h1>
                      <div className="cohort-layout-sidebar-status">
                        {intl.formatMessage(messages.sidebarMemberStatus, { count: detail.member_count })}
                      </div>
                      <div className="cohort-layout-sidebar-nav-title">
                        {intl.formatMessage(messages.navHeading)}
                      </div>
                      <ul className="cohort-layout-nav">
                        {navViews.map((view) => (
                          <li key={view}>
                            <NavLink
                              end={view === 'members'}
                              to={navTo(view)}
                              className={({ isActive }) => (
                                `cohort-layout-nav-link${isActive ? ' cohort-layout-nav-link-active' : ''}`
                              )}
                            >
                              {viewLabel(view)}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    </aside>
                    <div className="cohort-layout-content">
                      <CohortLayoutContext.Provider value={contextValue}>
                        <Outlet />
                      </CohortLayoutContext.Provider>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          {footerContent}
        </div>
      </div>
    </>
  );
};

export default CohortLayout;
